// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState } from 'react';
import { Stack, useLocalSearchParams } from 'expo-router';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { useUser } from '@/app/user-context';
import {
  adoptHoleSource,
  type HoleSourceAdoption,
} from '@/features/course-trace/adoptHoleSource';
import {
  buildCourseCardTable,
  type CourseCardCell,
  type CourseCardStated,
} from '@/features/course-trace/courseCardRows';
import { lengthSourceLabel } from '@/features/course-trace/lengthSourceLabel';
import { useCourseCard, type CourseTee } from '@/features/course-trace/useCourseCard';
import { formatHoleDistance, type DistanceUnit } from '@/features/units/distance';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import appColors from '@/theme/colors';

/** A cell nobody has a number for. Not a zero, and not a blank that reads as one. */
const EMPTY = '—';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * One course's card: its tees with their ratings, and every hole with par, stroke index and
 * the length from each tee.
 *
 * Its own screen rather than a section of the admin list's detail popup, because the thing
 * being read is a table as wide as the course has tees — the shape a popup on a phone has the
 * least of. Read-only bar one thing: the "Use …" button under a column makes that source's
 * numbers the official ones, exactly as if an admin had typed them into the course-config form.
 * Corrections still happen there, and our own measurement by moving the marker on the hole map.
 */
export default function CourseScorecardScreen() {
  const { t } = useI18n();
  const { courseId } = useLocalSearchParams<{ courseId?: string }>();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const distanceUnit = useDistanceUnit();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  // Admin-only, like the panel it is reached from: the lengths below are every stored number
  // with the source named, card imports included, which CLAUDE.md §19 keeps for review rather
  // than for a player to read as ours.
  const id = isAdmin && typeof courseId === 'string' && courseId.length > 0 ? courseId : null;

  const { state, refresh } = useCourseCard(apiBaseUrl, user?.token, id);
  const card = state.status === 'ready' ? state.card : null;
  const table = useMemo(
    () => (card ? buildCourseCardTable(card.holes, card.tees) : null),
    [card]
  );

  // The column being made official right now, by its key, so its button alone says so.
  const [adopting, setAdopting] = useState<string | null>(null);
  const [adoptFailed, setAdoptFailed] = useState(false);

  /**
   * One column, made official. The server does the copying — this only names the column — and
   * the course is re-read afterwards so the table shows what was stored, not what was asked.
   */
  const adopt = async (columnKey: string, adoption: HoleSourceAdoption) => {
    if (!id || !user?.token || adopting !== null) return;
    setAdopting(columnKey);
    setAdoptFailed(false);
    const ok = await adoptHoleSource(apiBaseUrl, user.token, id, adoption);
    setAdopting(null);
    if (ok) refresh();
    else setAdoptFailed(true);
  };

  const adoptButton = (
    columnKey: string,
    source: string | null,
    adoption: (source: string) => HoleSourceAdoption
  ) =>
    source === null ? null : (
      <Pressable
        style={[styles.adoptButton, adopting !== null ? styles.adoptButtonBusy : null]}
        accessibilityRole="button"
        disabled={adopting !== null}
        onPress={() => void adopt(columnKey, adoption(source))}
        testID={`course-scorecard-adopt-${columnKey}`}
      >
        <Text style={styles.adoptButtonText}>
          {adopting === columnKey
            ? t('courseScorecardPage.adopting')
            : t('courseScorecardPage.useSource', { source: sourceLabel(source, t) })}
        </Text>
      </Pressable>
    );

  return (
    <>
      <Stack.Screen options={{ title: t('courseScorecardPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{card?.name || t('courseScorecardPage.title')}</Text>

        {card ? (
          <>
            <Text style={styles.intro} testID="course-scorecard-club">
              {[card.clubName, card.city].filter(Boolean).join(' · ')}
            </Text>
            <Text style={styles.intro}>
              {t('courseScorecardPage.courseMeta', {
                holes: card.holes.length,
                par: table?.parTotal ?? 0,
                visibility: card.visibility,
              })}
            </Text>
          </>
        ) : null}

        {!isAdmin ? (
          <Text style={styles.paragraph} testID="course-scorecard-forbidden">
            {t('courseAnalysis.adminOnly')}
          </Text>
        ) : state.status === 'loading' || state.status === 'idle' ? (
          <Text style={styles.paragraph}>{t('courseScorecardPage.loading')}</Text>
        ) : state.status === 'forbidden' ? (
          <Text style={styles.paragraph} testID="course-scorecard-forbidden">
            {t('courseAnalysis.adminOnly')}
          </Text>
        ) : state.status === 'failed' ? (
          <>
            <Text style={styles.paragraph} testID="course-scorecard-failed">
              {t('courseScorecardPage.failed')}
            </Text>
            <Pressable style={styles.button} accessibilityRole="button" onPress={refresh}>
              <Text style={styles.buttonText}>{t('courseScorecardPage.retry')}</Text>
            </Pressable>
          </>
        ) : (
          <>
            <TeeTable tees={card?.tees ?? []} t={t} />

            {table === null ? (
              <Text style={styles.paragraph} testID="course-scorecard-no-holes">
                {t('courseScorecardPage.noHoles')}
              </Text>
            ) : (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('courseScorecardPage.holesTitle')}</Text>
                {/* As wide as the course has tees. Scrolling sideways is the honest answer on a
                    phone — the alternative is a column too narrow to read a length in. */}
                <ScrollView horizontal showsHorizontalScrollIndicator>
                  <View testID="course-scorecard-holes">
                    <View style={[styles.row, styles.headerRow]}>
                      <Text style={[styles.cell, styles.holeCell, styles.headingText]}>
                        {t('courseScorecardPage.hole')}
                      </Text>
                      <Text style={[styles.cell, styles.headingText]}>
                        {t('courseScorecardPage.par')}
                      </Text>
                      <Text style={[styles.cell, styles.headingText]}>
                        {t('courseScorecardPage.strokeIndex')}
                      </Text>
                      {/* Sits immediately right of the two columns it is the comparison for,
                          and only appears once something has been stated — an empty column on
                          every course in the catalogue would be noise on all of them. */}
                      {table.cardSources.length > 0 ? (
                        <View style={[styles.headerCell, styles.cardCell]}>
                          <Text
                            style={[styles.cell, styles.headingText]}
                            testID="course-scorecard-card-heading"
                          >
                            {t('courseScorecardPage.cardColumn')}
                          </Text>
                          {adoptButton('card', table.cardAdoptableSource, (source) => ({
                            source,
                            target: 'card',
                          }))}
                        </View>
                      ) : null}
                      {table.columns.map((column) => (
                        <View key={column.key} style={[styles.headerCell, styles.lengthCell]}>
                          <Text
                            style={[styles.cell, styles.headingText]}
                            testID={`course-scorecard-column-${column.key}`}
                          >
                            {column.tee
                              ? teeLabel(column.tee, t)
                              : t('courseScorecardPage.holeColumn')}
                          </Text>
                          {adoptButton(column.key, column.adoptableSource, (source) =>
                            column.tee
                              ? { source, target: 'teeLength', teeId: column.tee.id }
                              : { source, target: 'holeLength' }
                          )}
                        </View>
                      ))}
                    </View>

                    {table.rows.map((row) => (
                      <View
                        key={row.number}
                        style={styles.row}
                        testID={`course-scorecard-hole-${row.number}`}
                      >
                        <Text style={[styles.cell, styles.holeCell, styles.strongText]}>
                          {row.number}
                        </Text>
                        <Text style={styles.cell}>{row.par ?? EMPTY}</Text>
                        <Text style={styles.cell}>{row.strokeIndex ?? EMPTY}</Text>
                        {table.cardSources.length > 0 ? (
                          <Text
                            style={[
                              styles.cell,
                              styles.cardCell,
                              row.stated?.differs ? styles.differs : null,
                            ]}
                            testID={`course-scorecard-card-${row.number}`}
                          >
                            {formatCard(row.stated, t)}
                          </Text>
                        ) : null}
                        {row.cells.map((cell, index) => (
                          <Text
                            key={table.columns[index].key}
                            style={[styles.cell, styles.lengthCell]}
                            testID={`course-scorecard-cell-${row.number}-${table.columns[index].key}`}
                          >
                            {formatCell(cell, distanceUnit, t)}
                          </Text>
                        ))}
                      </View>
                    ))}

                    <View style={[styles.row, styles.totalRow]} testID="course-scorecard-total">
                      <Text style={[styles.cell, styles.holeCell, styles.strongText]}>
                        {t('courseScorecardPage.total')}
                      </Text>
                      <Text style={[styles.cell, styles.strongText]}>
                        {table.parTotal ?? EMPTY}
                      </Text>
                      <Text style={styles.cell}>{EMPTY}</Text>
                      {table.cardSources.length > 0 ? (
                        <Text style={[styles.cell, styles.cardCell]}>{EMPTY}</Text>
                      ) : null}
                      {table.columnTotals.map((total, index) => (
                        <Text
                          key={table.columns[index].key}
                          style={[styles.cell, styles.lengthCell, styles.strongText]}
                        >
                          {total === null ? EMPTY : formatHoleDistance(total, distanceUnit)}
                        </Text>
                      ))}
                    </View>
                  </View>
                </ScrollView>

                {/* Which numbers these are. §19 keeps a club's card out of a player's view and
                    keeps it for exactly this: an admin comparing what was imported against what
                    we measured. Saying so per cell is what makes it a comparison. */}
                <Text style={styles.note} testID="course-scorecard-sources">
                  {table.sources.length === 0
                    ? t('courseScorecardPage.noLengths')
                    : t('courseScorecardPage.sourcesNote', {
                        sources: table.sources.map((source) => sourceLabel(source, t)).join(', '),
                      })}
                </Text>

                {/* The same sentence for the card. Worth its own line rather than folded into
                    the one above: a length and a par are refused for different reasons — §19
                    keeps an imported length off a player's screen, §18 keeps an imported par
                    from replacing a person's — and an admin reading one is not reading the
                    other. */}
                <Text style={styles.note} testID="course-scorecard-card-sources">
                  {table.cardSources.length === 0
                    ? t('courseScorecardPage.noCards')
                    : t('courseScorecardPage.cardsNote', {
                        sources: table.cardSources
                          .map((source) => sourceLabel(source, t))
                          .join(', '),
                      })}
                </Text>

                {table.differingHoles > 0 ? (
                  <Text
                    style={[styles.note, styles.differs]}
                    testID="course-scorecard-card-differs"
                  >
                    {t('courseScorecardPage.cardsDifferNote', {
                      count: table.differingHoles,
                      total: table.rows.length,
                    })}
                  </Text>
                ) : null}

                {/* What the buttons do, said once — only when there is one to press. */}
                {table.cardAdoptableSource !== null ||
                table.columns.some((column) => column.adoptableSource !== null) ? (
                  <Text style={styles.note} testID="course-scorecard-adopt-note">
                    {t('courseScorecardPage.adoptNote')}
                  </Text>
                ) : null}
                {adoptFailed ? (
                  <Text style={[styles.note, styles.differs]} testID="course-scorecard-adopt-failed">
                    {t('courseScorecardPage.adoptFailed')}
                  </Text>
                ) : null}
              </View>
            )}
          </>
        )}
      </ScrollView>
    </>
  );
}

/** "Yellow (men)" — the sex is part of the identity, not a detail: it carries its own rating. */
const teeLabel = (tee: CourseTee, t: Translate): string => {
  const sex = tee.sex ? t(`courseScorecardPage.sex.${tee.sex}`) : '';
  const name = tee.name || t('courseScorecardPage.unnamedTee');
  return sex && !sex.startsWith('courseScorecardPage.') ? `${name} (${sex})` : name;
};

const sourceLabel = (source: string, t: Translate): string => lengthSourceLabel(source, t);

const formatCell = (cell: CourseCardCell, unit: DistanceUnit, t: Translate): string =>
  cell === null ? EMPTY : `${formatHoleDistance(cell.lengthMeters, unit)} · ${sourceLabel(cell.source, t)}`;

/** "5 / 17 · Golf i Sverige" — the stated par, its stroke index, and who stated them. */
const formatCard = (stated: CourseCardStated, t: Translate): string =>
  stated === null
    ? EMPTY
    : `${stated.par} / ${stated.strokeIndex} · ${sourceLabel(stated.source, t)}`;

/**
 * The tees themselves: course rating, slope and the tee's own par — what a playing handicap is
 * computed from, and the numbers a club publishes on the back of its card.
 */
function TeeTable({ tees, t }: { tees: readonly CourseTee[]; t: Translate }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{t('courseScorecardPage.teesTitle')}</Text>
      {tees.length === 0 ? (
        <Text style={styles.paragraph} testID="course-scorecard-no-tees">
          {t('courseScorecardPage.noTees')}
        </Text>
      ) : (
        <View testID="course-scorecard-tees">
          <View style={[styles.row, styles.headerRow]}>
            <Text style={[styles.cell, styles.teeCell, styles.headingText]}>
              {t('courseScorecardPage.tee')}
            </Text>
            <Text style={[styles.cell, styles.headingText]}>
              {t('courseScorecardPage.courseRating')}
            </Text>
            <Text style={[styles.cell, styles.headingText]}>
              {t('courseScorecardPage.slopeRating')}
            </Text>
            <Text style={[styles.cell, styles.headingText]}>{t('courseScorecardPage.par')}</Text>
          </View>
          {tees.map((tee) => (
            <View key={tee.id} style={styles.row} testID={`course-scorecard-tee-${tee.id}`}>
              <Text style={[styles.cell, styles.teeCell, styles.strongText]}>
                {teeLabel(tee, t)}
              </Text>
              <Text style={styles.cell}>{tee.courseRating ?? EMPTY}</Text>
              <Text style={styles.cell}>{tee.slopeRating ?? EMPTY}</Text>
              <Text style={styles.cell}>{tee.par ?? EMPTY}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { padding: 16, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, lineHeight: 18 },
  paragraph: { color: appColors.text.secondary, marginVertical: 8 },
  section: { marginTop: 16, gap: 8 },
  sectionTitle: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  note: { color: appColors.text.secondary, fontSize: 12, lineHeight: 17 },
  button: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignSelf: 'flex-start',
  },
  buttonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 12 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: appColors.border.default,
  },
  headerRow: { borderBottomWidth: 1 },
  totalRow: { borderBottomWidth: 0 },
  // Numbers line up down the column, so a cell that disagrees with the one above it is
  // visible without reading either.
  cell: {
    minWidth: 56,
    color: appColors.text.secondary,
    fontSize: 12,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
  holeCell: { minWidth: 44, textAlign: 'left' },
  teeCell: { minWidth: 140, textAlign: 'left' },
  lengthCell: { minWidth: 130 },
  cardCell: { minWidth: 130 },
  // A heading with a button under it: right-aligned like the numbers it heads.
  headerCell: { alignItems: 'flex-end', gap: 4 },
  adoptButton: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  adoptButtonBusy: { opacity: 0.5 },
  adoptButtonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 11 },
  // A disagreement is the one thing on this screen somebody has to act on, so it is the one
  // thing that is not the same grey as everything around it.
  differs: { color: appColors.status.warning },
  strongText: { color: appColors.text.primary, fontWeight: '600' },
  headingText: { color: appColors.text.secondary, fontSize: 11, fontWeight: '700' },
});
